import { Seo } from '@/components/Seo';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, User, FileText, Phone, Mail, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function ImpressumPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Seo
        title="Impressum"
        description="Impressum von marKIpilot – betrieben von der MYSYMP AG, Oberdorfstrasse 18a, 6207 Nottwil, Schweiz. Handelsregister CHE-192.170.455."
        path="/impressum"
      />
      {/* Navigation - with iPhone Safe Area */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content - with safe area padding */}
      <div className="pb-16" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 5rem)', paddingBottom: 'max(4rem, env(safe-area-inset-bottom))' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-8">
              Impressum
            </h1>

            <div className="space-y-8">
              {/* Company Info */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-teal-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Kontaktdaten</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-white font-semibold text-lg mb-2">MYSYMP AG</p>
                    <p className="text-slate-400">Oberdorfstrasse 18a</p>
                    <p className="text-slate-400">6207 Nottwil</p>
                    <p className="text-slate-400">Schweiz</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-teal-400" />
                      <a href="mailto:info@mysymp.ch" className="text-slate-400 hover:text-teal-400 transition-colors">
                        info@mysymp.ch
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-teal-400" />
                      <a href="https://www.mysymp.ch" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-teal-400 transition-colors">
                        www.mysymp.ch
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-teal-400" />
                      <a href="tel:+41794710625" className="text-slate-400 hover:text-teal-400 transition-colors">
                        +41 79 471 06 25
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Representative */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Vertretungsberechtigte Personen</h2>
                </div>
                <p className="text-white font-semibold">Isidoro Celentano</p>
                <p className="text-slate-400">CEO / Founder</p>
              </section>

              {/* Registration */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Handelsregistereintrag</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-500 text-sm mb-1">Eingetragener Firmenname</p>
                    <p className="text-white">MYSYMP AG</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm mb-1">Handelsregister-Nr.</p>
                    <p className="text-white">CHE-192.170.455</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm mb-1">Handelsregisteramt</p>
                    <p className="text-white">Nottwil</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm mb-1">Mehrwertsteuernummer</p>
                    <p className="text-white">CHE-192.170.455 MWST</p>
                  </div>
                </div>
              </section>

              {/* Disclaimer */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <h2 className="font-display text-xl font-bold mb-4">Haftungsausschluss</h2>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    Der Autor übernimmt keinerlei Gewähr hinsichtlich der inhaltlichen Richtigkeit, Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen.
                  </p>
                  <p>
                    Haftungsansprüche gegen den Autor wegen Schäden materieller oder immaterieller Art, welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten Informationen, durch Missbrauch der Verbindung oder durch technische Störungen entstanden sind, werden ausgeschlossen.
                  </p>
                  <p>
                    Alle Angebote sind unverbindlich. Der Autor behält es sich ausdrücklich vor, Teile der Seiten oder das gesamte Angebot ohne gesonderte Ankündigung zu verändern, zu ergänzen, zu löschen oder die Veröffentlichung zeitweise oder endgültig einzustellen.
                  </p>
                </div>
              </section>

              {/* Copyright */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <h2 className="font-display text-xl font-bold mb-4">Urheberrechte</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien auf der Website gehören ausschliesslich der MYSYMP AG oder den speziell genannten Rechtsinhabern. Für die Reproduktion jeglicher Elemente ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs">© {new Date().getFullYear()} marKIpilot – MYSYMP AG</span>
            <div className="flex items-center gap-4">
              <Link to="/datenschutz" className="text-slate-500 hover:text-slate-300 text-xs">Datenschutz</Link>
              <Link to="/agb" className="text-slate-500 hover:text-slate-300 text-xs">AGB</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
